<template>
  <div class="page-content">
    <div class="profile-container">
      <div class="profile-header">
        <div class="profile-card">
          <div class="profile-avatar-section">
            <div class="avatar-container">
              <img v-if="user?.photoURL" :src="user.photoURL" :alt="user.displayName || 'User'" class="profile-avatar" />
              <div v-else class="profile-avatar avatar-placeholder">
                {{ getInitials() }}
              </div>
              <button class="edit-avatar-btn" title="Change Profile Picture">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="profile-info">
            <h1 class="profile-name">{{ user?.displayName || 'User' }}</h1>
            <p class="profile-email">{{ user?.email }}</p>
            <div class="profile-stats">
              <div class="stat-item">
                <span class="stat-value">{{ totalRoutes }}</span>
                <span class="stat-label">Routes Created</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ totalLocations }}</span>
                <span class="stat-label">Locations Managed</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ joinedDate }}</span>
                <span class="stat-label">Member Since</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="profile-content">
        <div class="profile-sections">
          <div class="section-tabs">
            <button 
              v-for="tab in tabs" 
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="{ active: activeTab === tab.id }"
              class="tab-button"
            >
              <svg :width="18" :height="18" :viewBox="tab.icon.viewBox" fill="none" stroke="currentColor" stroke-width="2">
                <path :d="tab.icon.path"/>
                <path v-if="tab.icon.path2" :d="tab.icon.path2"/>
              </svg>
              {{ tab.label }}
            </button>
          </div>

          <div class="tab-content">
            <!-- Account Settings Tab -->
            <div v-if="activeTab === 'account'" class="tab-panel">
              <div class="settings-grid">
                <div class="setting-group">
                  <h3>Account Information</h3>
                  <div class="setting-item">
                    <label>Display Name</label>
                    <div class="input-group">
                      <input 
                        v-model="editableProfile.displayName" 
                        type="text" 
                        :readonly="!editMode"
                        class="profile-input"
                      />
                      <button v-if="!editMode" @click="toggleEditMode" class="edit-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div class="setting-item">
                    <label>Email Address</label>
                    <input 
                      :value="user?.email" 
                      type="email" 
                      readonly 
                      class="profile-input disabled"
                    />
                    <small class="help-text">Email cannot be changed</small>
                  </div>

                  <div class="setting-item">
                    <label>Account Type</label>
                    <div class="account-type">
                      <span class="type-badge">{{ getAccountType() }}</span>
                    </div>
                  </div>

                  <div v-if="editMode" class="setting-actions">
                    <button @click="saveProfile" class="save-btn" :disabled="saving">
                      <span v-if="saving">Saving...</span>
                      <span v-else>Save Changes</span>
                    </button>
                    <button @click="cancelEdit" class="cancel-btn">Cancel</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Tab -->
            <div v-if="activeTab === 'security'" class="tab-panel">
              <div class="settings-grid">
                <div class="setting-group">
                  <h3>Security Settings</h3>
                  <div class="setting-item">
                    <label>Password</label>
                    <button @click="changePassword" class="action-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <circle cx="12" cy="16" r="1"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Change Password
                    </button>
                    <small class="help-text">Last changed: {{ lastPasswordChange }}</small>
                  </div>

                  <div class="setting-item">
                    <label>Two-Factor Authentication</label>
                    <div class="toggle-setting">
                      <span>{{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}</span>
                      <button @click="toggleTwoFactor" class="toggle-btn" :class="{ enabled: twoFactorEnabled }">
                        {{ twoFactorEnabled ? 'Disable' : 'Enable' }}
                      </button>
                    </div>
                    <small class="help-text">Add an extra layer of security to your account</small>
                  </div>

                  <div class="setting-item">
                    <label>Active Sessions</label>
                    <div class="session-info">
                      <div class="session-item current">
                        <div class="session-details">
                          <span class="device">Current Device</span>
                          <span class="location">{{ userLocation }}</span>
                        </div>
                        <span class="session-status">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preferences Tab -->
            <div v-if="activeTab === 'preferences'" class="tab-panel">
              <div class="settings-grid">
                <div class="setting-group">
                  <h3>App Preferences</h3>
                  <div class="setting-item">
                    <label>Theme</label>
                    <select v-model="preferences.theme" class="profile-select">
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">System Default</option>
                    </select>
                  </div>

                  <div class="setting-item">
                    <label>Default Map View</label>
                    <select v-model="preferences.defaultMapView" class="profile-select">
                      <option value="street">Street View</option>
                      <option value="satellite">Satellite</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div class="setting-item">
                    <label>Units</label>
                    <select v-model="preferences.units" class="profile-select">
                      <option value="metric">Metric (km, kg)</option>
                      <option value="imperial">Imperial (mi, lbs)</option>
                    </select>
                  </div>

                  <div class="setting-item">
                    <label>Notifications</label>
                    <div class="checkbox-group">
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.emailNotifications" />
                        <span>Email notifications</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.routeAlerts" />
                        <span>Route optimization alerts</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.systemUpdates" />
                        <span>System updates</span>
                      </label>
                    </div>
                  </div>

                  <button @click="savePreferences" class="save-btn">Save Preferences</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { updateProfile } from 'firebase/auth'
import AuthService from '../services/AuthService'

export default {
  name: 'Profile',
  setup() {
    const user = ref(null)
    const activeTab = ref('account')
    const editMode = ref(false)
    const saving = ref(false)
    const twoFactorEnabled = ref(false)
    const totalRoutes = ref(0)
    const totalLocations = ref(0)
    const joinedDate = ref('')
    const lastPasswordChange = ref('Never')
    const userLocation = ref('Unknown')

    const editableProfile = reactive({
      displayName: ''
    })

    const preferences = reactive({
      theme: 'light',
      defaultMapView: 'street',
      units: 'metric',
      emailNotifications: true,
      routeAlerts: true,
      systemUpdates: false
    })

    const tabs = [
      {
        id: 'account',
        label: 'Account',
        icon: {
          viewBox: '0 0 24 24',
          path: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2',
          path2: 'M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'
        }
      },
      {
        id: 'security',
        label: 'Security',
        icon: {
          viewBox: '0 0 24 24',
          path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'
        }
      },
      {
        id: 'preferences',
        label: 'Preferences',
        icon: {
          viewBox: '0 0 24 24',
          path: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
          path2: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'
        }
      }
    ]

    const getInitials = () => {
      const name = user.value?.displayName || user.value?.email || 'User'
      return name.charAt(0).toUpperCase()
    }

    const getAccountType = () => {
      if (user.value?.providerData?.[0]?.providerId === 'google.com') {
        return 'Google Account'
      }
      return 'Email Account'
    }

    const toggleEditMode = () => {
      editMode.value = true
      editableProfile.displayName = user.value?.displayName || ''
    }

    const cancelEdit = () => {
      editMode.value = false
      editableProfile.displayName = user.value?.displayName || ''
    }

    const saveProfile = async () => {
      saving.value = true
      try {
        await updateProfile(user.value, {
          displayName: editableProfile.displayName
        })
        user.value.displayName = editableProfile.displayName
        editMode.value = false
      } catch (error) {
        console.error('Error updating profile:', error)
      } finally {
        saving.value = false
      }
    }

    const changePassword = () => {
      // Implement password change logic
      alert('Password change functionality would be implemented here')
    }

    const toggleTwoFactor = () => {
      twoFactorEnabled.value = !twoFactorEnabled.value
    }

    const savePreferences = () => {
      // Save preferences to localStorage or backend
      localStorage.setItem('userPreferences', JSON.stringify(preferences))
      alert('Preferences saved successfully!')
    }

    onMounted(() => {
      user.value = AuthService.getCurrentUser()
      if (user.value) {
        editableProfile.displayName = user.value.displayName || ''
        
        // Format joined date
        const creationTime = user.value.metadata?.creationTime
        if (creationTime) {
          joinedDate.value = new Date(creationTime).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })
        }

        // Load preferences from localStorage
        const savedPreferences = localStorage.getItem('userPreferences')
        if (savedPreferences) {
          Object.assign(preferences, JSON.parse(savedPreferences))
        }

        // Mock data for demo
        totalRoutes.value = Math.floor(Math.random() * 50) + 5
        totalLocations.value = Math.floor(Math.random() * 200) + 20
        userLocation.value = 'Unknown Location'
      }
    })

    return {
      user,
      activeTab,
      editMode,
      saving,
      twoFactorEnabled,
      totalRoutes,
      totalLocations,
      joinedDate,
      lastPasswordChange,
      userLocation,
      editableProfile,
      preferences,
      tabs,
      getInitials,
      getAccountType,
      toggleEditMode,
      cancelEdit,
      saveProfile,
      changePassword,
      toggleTwoFactor,
      savePreferences
    }
  }
}
</script>

<style scoped>
.page-content {
  flex: 1;
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  background: #f8fafc;
}

.profile-container {
  padding: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.profile-header {
  margin-bottom: 2rem;
}

.profile-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 2rem;
}

.profile-avatar-section {
  flex-shrink: 0;
}

.avatar-container {
  position: relative;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #e2e8f0;
}

.avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4285f4 0%, #667eea 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: 600;
  border: 4px solid #e2e8f0;
}

.edit-avatar-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4285f4;
}

.edit-avatar-btn:hover {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-size: 32px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 8px 0;
  font-family: 'Sansation', sans-serif;
}

.profile-email {
  color: #718096;
  font-size: 18px;
  margin: 0 0 24px 0;
}

.profile-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #4285f4;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
}

.profile-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.section-tabs {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.tab-button {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: transparent;
  color: #718096;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background: #edf2f7;
  color: #4285f4;
}

.tab-button.active {
  color: #4285f4;
  background: white;
  border-bottom-color: #4285f4;
}

.tab-content {
  padding: 0;
}

.tab-panel {
  padding: 2rem;
  min-height: 400px;
}

.settings-grid {
  max-width: 800px;
}

.setting-group {
  margin-bottom: 2rem;
}

.setting-group h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 24px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.setting-item {
  margin-bottom: 24px;
}

.setting-item label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  background: white;
}

.profile-input:focus {
  outline: none;
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}

.profile-input.disabled {
  background: #f7fafc;
  color: #718096;
  cursor: not-allowed;
}

.profile-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.profile-select:focus {
  outline: none;
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}

.edit-btn {
  padding: 8px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4285f4;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-btn:hover {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.help-text {
  color: #718096;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.account-type {
  margin-top: 4px;
}

.type-badge {
  display: inline-block;
  padding: 6px 12px;
  background: #e6f3ff;
  color: #4285f4;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.setting-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.save-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, #4285f4 0%, #667eea 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 12px 24px;
  background: transparent;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  color: #718096;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  border-color: #cbd5e0;
  background: #f7fafc;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  color: #4285f4;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #f7fafc;
  border-color: #4285f4;
}

.toggle-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.toggle-btn {
  padding: 8px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #718096;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn.enabled {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.toggle-btn:hover:not(.enabled) {
  border-color: #4285f4;
  color: #4285f4;
}

.session-info {
  margin-top: 8px;
}

.session-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.session-item.current {
  background: #e6f3ff;
  border-color: #4285f4;
}

.session-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device {
  font-weight: 600;
  color: #2d3748;
}

.location {
  font-size: 14px;
  color: #718096;
}

.session-status {
  padding: 4px 8px;
  background: #48bb78;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #4285f4;
}

.checkbox-item span {
  color: #2d3748;
  font-weight: 500;
}

@media (max-width: 768px) {
  .profile-container {
    padding: 1rem;
  }
  
  .profile-card {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
  
  .profile-stats {
    justify-content: center;
  }
  
  .section-tabs {
    flex-direction: column;
  }
  
  .tab-panel {
    padding: 1rem;
  }
  
  .setting-actions {
    flex-direction: column;
  }
}
</style>
